import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, BookOpenIcon, BriefcaseIcon, GraduationCapIcon, HeartHandshakeIcon, MessageSquareIcon, ShieldIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { GeometricAccent } from '../components/ui/GeometricAccent';
type LocalizedItem = {
  key: string;
  source: string;
  fallback: string;
  ctaKey?: string;
  ctaSource?: string;
  ctaFallback?: string;
  to: string;
  image?: string;
};
const childrenPrograms: LocalizedItem[] = [{
  key: 'home.children.kidsPrograms.title',
  source: 'Kids Programs',
  fallback: 'برامج الأطفال',
  ctaKey: 'home.children.kidsPrograms.cta',
  ctaSource: 'Explore',
  ctaFallback: 'استكشف',
  to: '/ar/children/kids-programs',
  image: 'https://cdn.magicpatterns.com/uploads/bVektgjSsyyb4QP9K5hGbC/kids-1.jpg'
}, {
  key: 'home.children.onlineLearning.title',
  source: 'Online Learning',
  fallback: 'التعلّم عبر الإنترنت',
  ctaKey: 'home.children.onlineLearning.cta',
  ctaSource: 'How it Works',
  ctaFallback: 'كيف يعمل',
  to: '/ar/children/online-learning',
  image: 'https://cdn.magicpatterns.com/uploads/jjNQUzfbm3fVgHUYbPU5fW/online-learning.jpg'
}, {
  key: 'home.children.valuesIdentity.title',
  source: 'Values & Identity',
  fallback: 'القيم والهوية',
  ctaKey: 'home.children.valuesIdentity.cta',
  ctaSource: 'Learn More',
  ctaFallback: 'اعرف المزيد',
  to: '/ar/children/values-identity',
  image: 'https://cdn.magicpatterns.com/uploads/hWerU51G2oHuKfx2gJNfg5/img2.jpg'
}, {
  key: 'home.children.scholarships.title',
  source: 'Scholarships',
  fallback: 'المنح الدراسية',
  ctaKey: 'home.children.scholarships.cta',
  ctaSource: 'Support Options',
  ctaFallback: 'خيارات الدعم',
  to: '/ar/children/scholarships',
  image: 'https://cdn.magicpatterns.com/uploads/8awkEnkAr35x3W2oQBLASf/Scholar-3.jpg'
}];
const adultPrograms: LocalizedItem[] = [{
  key: 'home.adults.annualPrograms.title',
  source: 'Annual Programs',
  fallback: 'البرامج السنوية',
  ctaKey: 'home.adults.annualPrograms.cta',
  ctaSource: 'Explore',
  ctaFallback: 'استكشف',
  to: '/ar/adults/annual-programs',
  image: 'https://cdn.magicpatterns.com/uploads/2oyAijm6fxaEJiR4qbNAKH/AnnualPrograms6.jpg'
}, {
  key: 'home.adults.coreCourses.title',
  source: 'Core Courses',
  fallback: 'الدورات الأساسية',
  ctaKey: 'home.adults.coreCourses.cta',
  ctaSource: 'Browse',
  ctaFallback: 'تصفّح',
  to: '/ar/adults/core-courses',
  image: 'https://cdn.magicpatterns.com/uploads/1KJnu5Mw1PZbQZTNxTbn3x/CoreCourses3.jpg'
}, {
  key: 'home.adults.professionalDevelopment.title',
  source: 'Professional Development',
  fallback: 'التطوير المهني',
  ctaKey: 'home.adults.professionalDevelopment.cta',
  ctaSource: 'View',
  ctaFallback: 'عرض',
  to: '/ar/adults/professional-development',
  image: 'https://cdn.magicpatterns.com/uploads/mZ6s4PwoMdkaamZH9rLkBU/Prof-Dev.jpg'
}, {
  key: 'home.adults.creativeLearning.title',
  source: 'Creative Learning',
  fallback: 'التعلّم الإبداعي',
  ctaKey: 'home.adults.creativeLearning.cta',
  ctaSource: 'Discover',
  ctaFallback: 'اكتشف',
  to: '/ar/adults/creative-learning',
  image: 'https://cdn.magicpatterns.com/uploads/ninMqHXNBrWb99GzWxD16c/Creative2.jpg'
}];
const partnershipOptions: LocalizedItem[] = [{
  key: 'home.partnership.donate.title',
  source: 'Donate For Children',
  fallback: 'تبرّع للأطفال',
  ctaKey: 'home.partnership.donate.cta',
  ctaSource: 'Donate',
  ctaFallback: 'تبرّع',
  to: '/ar/partnership/donate-for-children'
}, {
  key: 'home.partnership.scholarships.title',
  source: 'Scholarships Program',
  fallback: 'برنامج المنح الدراسية',
  ctaKey: 'home.partnership.scholarships.cta',
  ctaSource: 'Explore',
  ctaFallback: 'استكشف',
  to: '/ar/partnership/scholarships'
}, {
  key: 'home.partnership.institutionalPartners.title',
  source: 'Institutional Partners',
  fallback: 'الشركاء المؤسسيون',
  ctaKey: 'home.partnership.institutionalPartners.cta',
  ctaSource: 'Request A Brief',
  ctaFallback: 'اطلب نبذة',
  to: '/ar/partnership/institutional-partners'
}];
type ContactOption = {
  key: string;
  source: string;
  fallback: string;
  descriptionKey: string;
  descriptionSource: string;
  descriptionFallback: string;
  to: string;
  icon: typeof MessageSquareIcon;
};
const contactOptions: ContactOption[] = [{
  key: 'home.contact.generalInquiry.title',
  source: 'General Inquiry',
  fallback: 'استفسار عام',
  descriptionKey: 'home.contact.generalInquiry.description',
  descriptionSource: 'Questions about programs, organization or partnerships',
  descriptionFallback: 'أسئلة حول البرامج أو المؤسسة أو الشراكات',
  to: '/ar/contact#general-inquiry',
  icon: MessageSquareIcon
}, {
  key: 'home.contact.teacher.title',
  source: 'Become A Teacher',
  fallback: 'كن معلّمًا',
  descriptionKey: 'home.contact.teacher.description',
  descriptionSource: 'Share your expertise with our learners',
  descriptionFallback: 'شارك خبراتك مع متعلمينا',
  to: '/ar/contact#become-a-teacher',
  icon: GraduationCapIcon
}, {
  key: 'home.contact.volunteers.title',
  source: 'Volunteers',
  fallback: 'المتطوعون',
  descriptionKey: 'home.contact.volunteers.description',
  descriptionSource: 'Give your time to support families',
  descriptionFallback: 'قدّم وقتك لدعم الأسر',
  to: '/ar/contact#volunteers',
  icon: HeartHandshakeIcon
}, {
  key: 'home.contact.careers.title',
  source: 'Careers',
  fallback: 'الوظائف',
  descriptionKey: 'home.contact.careers.description',
  descriptionSource: 'Join our team and grow with us successfully',
  descriptionFallback: 'انضم إلى فريقنا وواصل نموك معنا',
  to: '/ar/contact#careers',
  icon: BriefcaseIcon
}];
export function ArabicHome() {
  const t = (_key: string, _source: string, arabicCopy: string) => arabicCopy;
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-2590" data-mp="2l0piucezwdap">
      <section className="relative overflow-hidden bg-ivory" data-id="element-2591" data-mp="2l2ae6494w09m">
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-2592" data-mp="2pz922527fazd" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-10 lg:pb-28 lg:pt-12" data-id="element-2593" data-mp="3dpwi5dm9x6x0">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-2594" data-mp="3in4w120e2osg">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col gap-7 lg:col-span-6" data-id="element-2595" data-mp="34pggm9p3c9us">
              <span className="inline-flex self-start rounded-full bg-sage-100 px-3 py-1.5 text-[11px] font-semibold text-forest-700" data-id="element-2596" data-mp="1xb4x5lrgj1xq">
                {t('home.hero.eyebrow', 'International Islamic Learning Hub', 'المركز الدولي للتعلّم الإسلامي')}
              </span>
              <h1 className="font-display text-[2.1rem] font-semibold leading-[1.45] text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-2597" data-mp="1ks9a5hz7nkzc">
                {t('home.hero.title', 'Empowering Muslim Children And Families in Non-Muslim Countries', 'تمكين الأطفال والأسر المسلمة في الدول غير المسلمة')}
              </h1>
              <p className="max-w-xl text-base leading-loose text-ink-700 md:text-lg" data-id="element-2598" data-mp="123cepas2umfj">
                {t('home.hero.description', 'We help Muslim children grow with confidence, identity, and dignity while integrating successfully into non-Muslim societies.', 'نساعد الأطفال المسلمين على النمو بثقة وهوية وكرامة، مع الاندماج الناجح في المجتمعات غير المسلمة.')}
              </p>
              <p className="text-[15px] font-medium leading-loose text-forest-700" data-id="element-2599" data-mp="3txl21ehml6rj">
                {t('home.hero.statement', 'Children first. Education with protection. Integration without loss of identity.', 'الأطفال أولًا. تعليمٌ يحمي. واندماجٌ دون فقدان الهوية.')}
              </p>
              <div className="flex flex-wrap gap-3 pt-2" data-id="element-2600" data-mp="btjemh36kfht">
                <Link to="/ar/children" data-id="element-2601" data-mp="grkyh48fkiuc">
                  <Button variant="primary" size="lg" data-id="element-2602" data-mp="gqjj4nkdta6t">
                    {t('home.hero.childrenCta', 'For Children', 'للأطفال')}
                  </Button>
                </Link>
                <Link to="/ar/adults" data-id="element-2603" data-mp="qi772ehqc93j">
                  <Button variant="secondary" size="lg" data-id="element-2604" data-mp="qj8iik2nqxls">
                    {t('home.hero.adultsCta', 'For Adults', 'للبالغين')}
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-2605" data-mp="14gdwd68m50rl">
              <div className="relative mx-auto max-w-[520px]" data-id="element-2606" data-mp="2k1pkme2mefu1">
                <img src="https://cdn.magicpatterns.com/uploads/hGHm8r6v44K3dzDxzS719q/Hero-Image-2.png" alt="عائلة مسلمة مبتسمة داخل قوس زخرفي" className="h-auto w-full object-contain" data-id="element-2607" data-mp="2fyt0uk1cgdoi" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="relative overflow-hidden bg-[#EDF2F7]/60 py-20 md:py-28" data-id="element-2608" data-mp="2ywvtr9x0iurl">
        <Container data-id="element-2609" data-mp="2yuj2251gd61c">
          <div className="grid items-center gap-12 lg:grid-cols-12" data-id="element-2610" data-mp="2ty101cxiblsp">
            <div className="lg:col-span-6" data-id="element-2611" data-mp="2tvol78n1prjv">
              <span className="inline-flex rounded-full bg-sage-100 px-3 py-1.5 text-[11px] font-semibold text-forest-700" data-id="element-2612" data-mp="3rdexb4l6tc9x">
                {t('home.why.eyebrow', 'Why We Exist', 'لماذا نعمل')}
              </span>
              <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2613" data-mp="125deto9sgawm">
                {t('home.why.title', 'Why This Initiative Matters', 'لماذا تهم هذه المبادرة')}
              </h2>
              <p className="mt-5 max-w-xl text-base leading-loose text-ink-700 md:text-lg" data-id="element-2614" data-mp="1b6bnwkz338m2">
                {t('home.why.description', 'Millions of Muslim families face new realities every day. Muslim children are at risk when raised in non-Muslim communities without access to faith-aware educational and community-based support.', 'تواجه ملايين الأسر المسلمة واقعًا جديدًا كل يوم. ويكون الأطفال المسلمون أكثر عرضة للتحديات حين ينشأون في مجتمعات غير مسلمة دون وصول إلى تعليم ودعم مجتمعي يراعي الهوية والإيمان.')}
              </p>
              <ul className="mt-8 max-w-xl space-y-3 text-ink-700" data-id="element-2615" data-mp="23rl9jg2k02vv">
                <Bullet data-id="element-2616" data-mp="1yvw6h2rbs3ut">
                  {t('home.why.pointOne', 'Most schools provide only academic learning.', 'توفر معظم المدارس تعليمًا أكاديميًا فقط.')}
                </Bullet>
                <Bullet data-id="element-2617" data-mp="29fyh9ck7dc88">
                  {t('home.why.pointTwo', 'Parents face uncertainty about belonging, identity, and integration.', 'يواجه الآباء والأمهات حيرة بشأن الانتماء والهوية والاندماج.')}
                </Bullet>
              </ul>
              <div className="mt-8" data-id="element-2618" data-mp="24hn8741qf5r2">
                <Link to="/ar/about" data-id="element-2619" data-mp="24ionjkps6eel">
                  <Button variant="gold" data-id="element-2620" data-mp="1ps7k5vtl5ef2">{t('home.why.cta', 'About Us', 'تعرّف علينا')}</Button>
                </Link>
              </div>
            </div>
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-2621" data-mp="1ur25p5ixth2v">
              <img src="https://cdn.magicpatterns.com/uploads/5BWDsGqFyCo2fwTbkPN1RM/learning.png" alt="رسم مائي لمسجد تحيط به النباتات" className="h-auto w-full object-contain" data-id="element-2622" data-mp="3nh4eoj6whnke" />
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="relative overflow-hidden bg-forest-700 py-20 text-ivory md:py-28" data-id="element-2623" data-mp="a8rmjcxfzr16">
        <div className="absolute inset-x-0 top-0 h-3 bg-[repeating-linear-gradient(45deg,rgba(232,217,176,0.4)_0_8px,transparent_8px_16px)]" data-id="element-2624" data-mp="59na8df81d6m" />
        <div className="absolute inset-x-0 bottom-0 h-3 bg-[repeating-linear-gradient(45deg,rgba(232,217,176,0.4)_0_8px,transparent_8px_16px)]" data-id="element-2625" data-mp="170c5uryiw0z" />
        <Container className="relative" data-id="element-2626" data-mp="3sgpbamylljty">
          <div className="mx-auto max-w-3xl text-center" data-id="element-2627" data-mp="2lykcbn7ugbya">
            <span className="inline-flex rounded-full bg-sand-300/20 px-3 py-1.5 text-[11px] font-semibold text-sand-200" data-id="element-2628" data-mp="2lvxzb826p48o">
              {t('home.children.eyebrow', 'For Children', 'للأطفال')}
            </span>
            <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.45] text-ivory md:text-5xl" data-id="element-2629" data-mp="11g05wesousul">
              {t('home.children.title', 'Children Are Our Core Responsibility', 'الأطفال هم مسؤوليتنا الأساسية')}
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-5 md:gap-7 lg:grid-cols-4" data-id="element-2630" data-mp="1aa4q4fdbh615">
            {childrenPrograms.map((program, index) => <motion.div key={program.key} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.08,
            ease: [0.22, 1, 0.36, 1]
          }} whileHover={{
            y: -4
          }} data-id="element-2631" data-mp="1k6r9ezpzvp12" data-mpi={index}>
                <Link to={program.to} className="group flex flex-col items-center text-center" data-id="element-2632" data-mp="3qz0fvvoud6zd">
                  <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[28px] border-2 border-sand-300/50 shadow-card" data-id="element-2633" data-mp="3uz2iq86bc6g8">
                    <img src={program.image} alt={t(program.key, program.source, program.fallback)} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-2634" data-mp="2vnaqgpjg2a7b" />
                    <div className="absolute inset-0 bg-gradient-to-t from-forest-800/60 via-transparent to-transparent" data-id="element-2635" data-mp="34lw88ih3dl71" />
                  </div>
                  <div className="mt-4" data-id="element-2636" data-mp="2c030rvkupidx">
                    <h3 className="font-display text-base font-semibold leading-loose text-ivory md:text-lg" data-id="element-2637" data-mp="2bwypyfcvroea">
                      {t(program.key, program.source, program.fallback)}
                    </h3>
                    <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-sand-300 px-4 py-1.5 text-xs font-medium text-ink-900 transition group-hover:bg-sand-400" data-id="element-2638" data-mp="2lvedy9gm2hjk">
                      {t(program.ctaKey || '', program.ctaSource || '', program.ctaFallback || '')}
                      <ArrowLeftIcon size={12} aria-hidden="true" data-id="element-2639" data-mp="8p6m5jsd23s6" />
                    </span>
                  </div>
                </Link>
              </motion.div>)}
          </div>
        </Container>
      </section>

      <section className="relative overflow-hidden bg-sand-50/60 py-20 md:py-28" data-id="element-2640" data-mp="3rep6bcuzrh4">
        <Container data-id="element-2641" data-mp="3p26tx36vf3j">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-2642" data-mp="rh8zal5sn1xo">
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-2643" data-mp="rf6csb3unqxp">
              <div className="relative aspect-square overflow-hidden rounded-3xl shadow-card ring-1 ring-sand-300/40" data-id="element-2644" data-mp="336tj7nihpht3">
                <img src="https://cdn.magicpatterns.com/uploads/w61trt4PHZuMs5cy1RMxTR/ForParents1.jpg" alt="أسرة مسلمة تقضي وقتًا سعيدًا معًا في الحديقة" className="h-full w-full object-cover" data-id="element-2645" data-mp="385wjyvrft1v9" />
              </div>
            </motion.div>
            <div className="lg:col-span-6" data-id="element-2646" data-mp="13yjsx2ngp5zs">
              <span className="inline-flex rounded-full bg-sage-100 px-3 py-1.5 text-[11px] font-semibold text-forest-700" data-id="element-2647" data-mp="13x8or1q3tw85">
                {t('home.parents.eyebrow', 'For Parents', 'للآباء والأمهات')}
              </span>
              <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.5] text-ink-900 md:text-4xl lg:text-[2.6rem]" data-id="element-2648" data-mp="q03ndf1c5lj8">
                {t('home.parents.title', 'Supporting Families Beyond The Classroom', 'دعم الأسر خارج إطار الصف الدراسي')}
              </h2>
              <div className="mt-7 flex flex-wrap gap-3" data-id="element-2649" data-mp="h18bpnfivj8j">
                <span className="inline-flex items-center gap-2 rounded-full bg-sage-100 px-4 py-2 text-sm font-medium text-forest-700" data-id="element-2650" data-mp="2fdqhaqqkzepi">
                  <ShieldIcon size={14} strokeWidth={1.6} aria-hidden="true" data-id="element-2651" data-mp="2bb2dixgq4xze" />
                  {t('home.parents.membership', 'Membership', 'العضوية')}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-sand-100 px-4 py-2 text-sm font-medium text-sand-500" data-id="element-2652" data-mp="22xkqf0i6vh9">
                  <BookOpenIcon size={14} strokeWidth={1.6} aria-hidden="true" data-id="element-2653" data-mp="awsp3ktswhvu" />
                  {t('home.parents.resources', 'Resources', 'الموارد')}
                </span>
              </div>
              <p className="mt-6 text-lg leading-loose text-ink-700" data-id="element-2654" data-mp="3aea7lckxrtaf">
                {t('home.parents.intro', 'Strong families grow stronger together.', 'تنمو الأسر القوية أكثر قوةً معًا.')}
              </p>
              <ul className="mt-5 space-y-3 text-ink-700" data-id="element-2655" data-mp="3obp0wseb8l7o">
                <Bullet data-id="element-2656" data-mp="3od9sc95bvo1b">{t('home.parents.pointOne', 'The Parent Membership is a balanced family support system.', 'عضوية الوالدين هي منظومة متوازنة لدعم الأسرة.')}</Bullet>
                <Bullet data-id="element-2657" data-mp="13kg5eno1gmco">{t('home.parents.pointTwo', 'Access to guidance without confusion.', 'الوصول إلى إرشاد واضح دون حيرة.')}</Bullet>
                <Bullet data-id="element-2658" data-mp="f95ui2vrch5j">{t('home.parents.pointThree', 'A safe place to ask, share, and learn.', 'مساحة آمنة للسؤال والمشاركة والتعلّم.')}</Bullet>
                <Bullet data-id="element-2659" data-mp="o1ra5a43e7q5">{t('home.parents.pointFour', 'Tools and resources for engagement.', 'أدوات وموارد لتعزيز المشاركة.')}</Bullet>
                <Bullet data-id="element-2660" data-mp="j8y3ggp2cpbf">{t('home.parents.pointFive', 'A weekly space to find rest, plan clearly, and walk with clarity and confidence.', 'مساحة أسبوعية للراحة والتخطيط بوضوح والمضي بثقة.')}</Bullet>
              </ul>
              <div className="mt-8" data-id="element-2661" data-mp="t02ej5bmbq0z">
                <Link to="/ar/parents" data-id="element-2662" data-mp="16ztq6zenwubt">
                  <Button variant="primary" data-id="element-2663" data-mp="16ysc0m2cnrpv">{t('home.parents.cta', 'Discover More', 'اكتشف المزيد')}</Button>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="relative overflow-hidden bg-sand-50/60 py-20 md:py-28" data-id="element-2664" data-mp="38rqcn42dbold">
        <Container data-id="element-2665" data-mp="34rfw4xgy9fs9">
          <div className="mx-auto max-w-3xl text-center" data-id="element-2666" data-mp="34ry24ltez8dd">
            <span className="inline-flex rounded-full border border-sand-300/50 bg-white px-3 py-1.5 text-[11px] font-semibold text-sand-500" data-id="element-2667" data-mp="2umzgo57du0oz">
              {t('home.adults.eyebrow', 'For Adults', 'للبالغين')}
            </span>
            <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2668" data-mp="2kvv2v8tg0lvq">
              {t('home.adults.title', 'Education For Responsible Participation', 'تعليم من أجل مشاركة مسؤولة')}
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4" data-id="element-2669" data-mp="2fxjvr5uj8ph7">
            {adultPrograms.map((program, index) => <motion.article key={program.key} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.08,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-card" data-id="element-2670" data-mp="21ytwbjk8qdpo" data-mpi={index}>
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl" data-id="element-2671" data-mp="18nc3bsb4xkyk">
                  <img src={program.image} alt={t(program.key, program.source, program.fallback)} className="h-full w-full object-cover" data-id="element-2672" data-mp="1dhyim6kc7xnx" />
                </div>
                <div className="flex flex-1 flex-col items-center justify-between gap-3 p-5 text-center" data-id="element-2673" data-mp="1rf4y9s9rlg8x">
                  <h3 className="font-display text-sm font-semibold leading-loose text-ink-900 md:text-base" data-id="element-2674" data-mp="1wezodtlpu1sx">
                    {t(program.key, program.source, program.fallback)}
                  </h3>
                  <Link to={program.to} data-id="element-2675" data-mp="1mfsan5b2k55a">
                    <Button variant="primary" size="sm" data-id="element-2676" data-mp="1meqqmbc2s8bf">
                      {t(program.ctaKey || '', program.ctaSource || '', program.ctaFallback || '')}
                    </Button>
                  </Link>
                </div>
              </motion.article>)}
          </div>
          <div className="relative mt-14 overflow-hidden rounded-3xl bg-forest-700 bg-cover bg-center px-6 py-12 text-center text-ivory shadow-card md:py-14" style={{
          backgroundImage: "url('https://cdn.magicpatterns.com/uploads/9YPK89mwYRQBaNVVDfCqxu/cta-five_bg2.png')"
        }} data-id="element-2677" data-mp="3g13n8n8bxs0">
            <div className="absolute inset-0 bg-forest-700/30" aria-hidden="true" data-id="element-2678" data-mp="8evvt1736yos" />
            <div className="relative" data-id="element-2679" data-mp="3gxa3nb43b7pc">
              <h3 className="font-display text-2xl font-semibold leading-[1.55] md:text-3xl" data-id="element-2680" data-mp="3bxp3wyh3ivbk">
                {t('home.adults.scholarshipTitle', "Accessing Islamic Knowledge Shouldn't be a Luxury", 'الوصول إلى المعرفة الإسلامية ليس رفاهية')}
              </h3>
              <p className="mx-auto mt-4 max-w-2xl leading-loose text-ivory/85" data-id="element-2681" data-mp="3luk4dmwxj5sh">
                {t('home.adults.scholarshipDescription', 'Join our initiative to fund scholarships for children of determination and children from families facing hardships in non-Muslim countries.', 'انضم إلى مبادرتنا لتمويل المنح الدراسية للأطفال من أصحاب الهمم وأبناء الأسر التي تواجه صعوبات في الدول غير المسلمة.')}
              </p>
              <div className="mt-6" data-id="element-2682" data-mp="3ro6s7g0u7mbx">
                <Link to="/ar/partnership/become-a-partner" data-id="element-2683" data-mp="hccqd5dyfbb">
                  <Button variant="gold" data-id="element-2684" data-mp="gu9gwkb30tw">{t('home.adults.scholarshipCta', 'Become A Partner', 'كن شريكًا')}</Button>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="relative overflow-hidden bg-[#F7EDE9]/60 py-20 md:py-28" data-id="element-2685" data-mp="33zvwera00cui">
        <Container data-id="element-2686" data-mp="38vufbzck52nj">
          <div className="mx-auto max-w-3xl text-center" data-id="element-2687" data-mp="38zi6iruj5cz3">
            <span className="inline-flex rounded-full bg-sage-100 px-3 py-1.5 text-[11px] font-semibold text-forest-700" data-id="element-2688" data-mp="3dx1pu6634fxw">
              {t('home.partnership.eyebrow', 'Our Support', 'دعمنا')}
            </span>
            <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2689" data-mp="t46j665dbe5s">
              {t('home.partnership.title', 'Partnering For Long-Term Impact', 'شراكات من أجل أثر مستدام')}
            </h2>
            <p className="mt-4 leading-loose text-ink-700" data-id="element-2690" data-mp="23y8vthanvqfc">
              {t('home.partnership.description', 'We collaborate with foundations, institutions, and community partners committed to strengthening Muslim families living as minorities.', 'نتعاون مع المؤسسات والجهات الداعمة وشركاء المجتمع الملتزمين بتعزيز الأسر المسلمة التي تعيش كأقليات.')}
            </p>
            <p className="mt-3 text-sm leading-loose text-ink-500" data-id="element-2691" data-mp="1kylap8g5i2hk">
              {t('home.partnership.supportingText', 'Together, we build sustainable structures that support future generations.', 'معًا نبني هياكل مستدامة تدعم الأجيال القادمة.')}
            </p>
          </div>
          <div className="mt-14 grid gap-7 md:grid-cols-3 md:gap-9" data-id="element-2692" data-mp="3ac87crws69rj">
            {partnershipOptions.map((option, index) => <motion.div key={option.key} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.6,
            delay: index * 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} whileHover={{
            y: -4
          }} className="group flex flex-col" data-id="element-2693" data-mp="3ndtu9bppshr9" data-mpi={index}>
                <Link to={option.to} className="flex flex-col rounded-3xl border-2 border-sand-300/80 bg-sage-100 px-7 py-8 shadow-soft ring-1 ring-inset ring-white/40 transition-all duration-300 hover:border-sand-400 hover:shadow-card" data-id="element-2694" data-mp="34fr772gq8uw3">
                  <h3 className="font-display text-xl font-semibold leading-[1.7] text-ink-900 md:text-2xl" data-id="element-2695" data-mp="2gp529k20rqqb">
                    {t(option.key, option.source, option.fallback)}
                  </h3>
                  <span className="mt-5 inline-flex w-fit items-center gap-1.5 rounded-full bg-sand-300 px-6 py-2.5 text-sm font-semibold text-ink-900 transition-colors duration-300 group-hover:bg-sand-400" data-id="element-2696" data-mp="222g3jkfldhkz">
                    {t(option.ctaKey || '', option.ctaSource || '', option.ctaFallback || '')}
                    <ArrowLeftIcon size={16} aria-hidden="true" data-id="element-2697" data-mp="2641olt2a5269" />
                  </span>
                </Link>
              </motion.div>)}
          </div>
        </Container>
      </section>

      <section className="relative bg-white/60 py-20 md:py-28" data-id="element-2698" data-mp="2g3it475spa0n">
        <Container data-id="element-2699" data-mp="2g0wk0339amgb">
          <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-2700" data-mp="2ttvxjzjha6l8">
            <div data-id="element-2701" data-mp="2pssi3e5ceckj">
              <span className="inline-flex rounded-full border border-sand-300/50 bg-white px-3 py-1.5 text-[11px] font-semibold text-sand-500" data-id="element-2702" data-mp="2psaj49rapk8x">
                {t('home.contact.eyebrow', 'Contact', 'تواصل معنا')}
              </span>
              <h2 className="mt-4 font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2703" data-mp="2yv1te68fq9yz">
                {t('home.contact.title', 'Get In Touch', 'تواصل معنا')}
              </h2>
            </div>
            <Link to="/ar/contact" data-id="element-2704" data-mp="20f1nn40ozzbd">
              <Button variant="primary" data-id="element-2705" data-mp="25g8vt46qz0ik">{t('home.contact.cta', 'Contact Us', 'اتصل بنا')}</Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4" data-id="element-2706" data-mp="2a8s87xtp7pls">
            {contactOptions.map((option, index) => {
            const Icon = option.icon;
            return <motion.a key={option.key} href={option.to} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.08,
              ease: [0.22, 1, 0.36, 1]
            }} whileHover={{
              y: -4
            }} className="group flex flex-col justify-between gap-8 rounded-2xl border border-sand-300/50 bg-white p-6 text-right shadow-soft transition-shadow hover:shadow-card" data-id="element-2707" data-mp="2k3k9pcqeiwlu" data-mpi={index}>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sage-100 text-forest-700" data-id="element-2708" data-mp="mgi2ubfqstoa">
                    <Icon size={22} strokeWidth={1.6} aria-hidden="true" data-id="element-2709" data-mp="cjn69y3153cn" />
                  </span>
                  <div data-id="element-2710" data-mp="1a7dn8nhvcmli">
                    <h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2711" data-mp="1a7nh4m61bdwh">
                      {t(option.key, option.source, option.fallback)}
                    </h3>
                    <p className="mt-1.5 text-sm leading-loose text-ink-500" data-id="element-2712" data-mp="108ofhnjosiiz">
                      {t(option.descriptionKey, option.descriptionSource, option.descriptionFallback)}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700" data-id="element-2713" data-mp="3kzoy40du5c2s">
                      {t('home.contact.getInTouch', 'Get in touch', 'تواصل معنا')}
                      <ArrowLeftIcon size={15} strokeWidth={2} aria-hidden="true" data-id="element-2714" data-mp="1jwxumemmvllj" />
                    </span>
                  </div>
                </motion.a>;
          })}
          </div>
        </Container>
      </section>
    </div>;
}
function Bullet({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-start gap-3 leading-loose" data-id="element-2715" data-mp="1fwf3ynwh8lkx">
      <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-forest-700" aria-hidden="true" data-id="element-2716" data-mp="23lgag5lr1mnk" />
      <span data-id="element-2717" data-mp="27mjqas3srios">{children}</span>
    </li>;
}